import {Card, FormControl, FormField, Option, Separator} from 'platform/components';
import {Grid, GridItem, Space, VStack} from 'platform/foundation';

import {FieldValues} from 'react-hook-form';

import {isString} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DEPOSIT_METHOD_PAYMENT_TYPES} from '../../../constants/DEPOSIT_METHOD_PAYMENT_TYPES';
import {PROPAGATED_DOC_SERIES_SECTIONS} from '../../../constants/PROPAGATED_DOC_SERIES_SECTIONS';
import {DepositDefaultPaymentType} from '../../../schemas/depositDefaultPaymentTypeSchema';
import {DepositFormBinding} from '../../../types/DepositFormBinding';
import {DepositMethodName} from '../../../types/DepositMethodName';
import {DocumentTemplateOptionGroup} from '../../../types/DocumentTemplateOptionGroup';

interface DepositPaymentMethodCardProps<TFieldValues extends FieldValues>
  extends RequiredTestIdProps {
  control: FormControl<TFieldValues>;
  binding: DepositFormBinding<TFieldValues>;
  method: DepositMethodName;
  title: string;
  isAllowed: boolean;
  defaultPaymentType: DepositDefaultPaymentType | Nullish;
  invoiceTemplateOptions: DocumentTemplateOptionGroup[];
  isTemplatesLoading?: boolean;
  docSeriesOptions: Option[];
  isDocSeriesLoading?: boolean;
}

export function DepositPaymentMethodCard<TFieldValues extends FieldValues>(
  props: DepositPaymentMethodCardProps<TFieldValues>
) {
  const handleIsAllowedChange = (isAllowed: boolean) => {
    props.binding.setMethodAllowed(props.method, isAllowed, {
      shouldDirty: true,
      shouldValidate: true,
    });

    const isDisablingDefaultMethod =
      !isAllowed && props.defaultPaymentType === DEPOSIT_METHOD_PAYMENT_TYPES[props.method];

    if (isDisablingDefaultMethod) {
      props.binding.setFieldValue('defaultPaymentType', null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const handleTaxDocumentDocSeriesChange = (value: string | number | null) => {
    const docSeriesId = isString(value) ? value : null;

    PROPAGATED_DOC_SERIES_SECTIONS.forEach((section) => {
      props.binding.setFieldValue(`${props.method}.${section}.docSeriesId`, docSeriesId, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  };

  return (
    <Card
      title={props.title}
      isExpanded={props.isAllowed}
      control={{
        type: 'switch',
        value: props.isAllowed,
        onChange: handleIsAllowedChange,
      }}
      variant="inlineGrey"
      data-testid={suffixTestId('card', props)}
    >
      <Separator spacing={0} />
      <Space vertical={4} />
      <VStack spacing={4}>
        <Grid columns={2} horizontalSpacing={6}>
          <GridItem>
            <FormField
              control={props.control}
              name={props.binding.getFieldName(`${props.method}.taxDocument.templateId`)}
              type="choice"
              label={i18n.t('entity.settings.labels.taxDocumentTemplate')}
              options={props.invoiceTemplateOptions}
              isLoading={props.isTemplatesLoading}
              isRequired
              isNotClearable
              data-testid={suffixTestId('taxDocumentTemplate', props)}
            />
          </GridItem>
        </Grid>
        <Grid columns={2} horizontalSpacing={6}>
          <FormField
            control={props.control}
            name={props.binding.getFieldName(`${props.method}.taxDocument.docSeriesId`)}
            type="choice"
            label={i18n.t('entity.settings.labels.taxDocument')}
            options={props.docSeriesOptions}
            isLoading={props.isDocSeriesLoading}
            isRequired
            isNotClearable
            onChange={handleTaxDocumentDocSeriesChange}
            data-testid={suffixTestId('taxDocument', props)}
          />
          <FormField
            control={props.control}
            name={props.binding.getFieldName(`${props.method}.taxDocumentCorrection.docSeriesId`)}
            type="choice"
            label={i18n.t('entity.settings.labels.correctiveDocument')}
            options={props.docSeriesOptions}
            isLoading={props.isDocSeriesLoading}
            isRequired
            isNotClearable
            data-testid={suffixTestId('correctiveDocument', props)}
          />
        </Grid>
        <Grid columns={2} horizontalSpacing={6}>
          <FormField
            control={props.control}
            name={props.binding.getFieldName(`${props.method}.taxDocumentEu.docSeriesId`)}
            type="choice"
            label={i18n.t('entity.settings.labels.taxDocumentEu')}
            options={props.docSeriesOptions}
            isLoading={props.isDocSeriesLoading}
            isRequired
            isNotClearable
            data-testid={suffixTestId('taxDocumentEu', props)}
          />
          <FormField
            control={props.control}
            name={props.binding.getFieldName(`${props.method}.taxDocumentCorrectionEu.docSeriesId`)}
            type="choice"
            label={i18n.t('entity.settings.labels.correctiveDocumentEu')}
            options={props.docSeriesOptions}
            isLoading={props.isDocSeriesLoading}
            isRequired
            isNotClearable
            data-testid={suffixTestId('correctiveDocumentEu', props)}
          />
        </Grid>
        <Grid columns={2} horizontalSpacing={6}>
          <FormField
            control={props.control}
            name={props.binding.getFieldName(`${props.method}.taxDocumentOutsideEu.docSeriesId`)}
            type="choice"
            label={i18n.t('entity.settings.labels.taxDocumentOutsideEu')}
            options={props.docSeriesOptions}
            isLoading={props.isDocSeriesLoading}
            isRequired
            isNotClearable
            data-testid={suffixTestId('taxDocumentOutsideEu', props)}
          />
          <FormField
            control={props.control}
            name={props.binding.getFieldName(
              `${props.method}.taxDocumentCorrectionOutsideEu.docSeriesId`
            )}
            type="choice"
            label={i18n.t('entity.settings.labels.correctiveDocumentOutsideEu')}
            options={props.docSeriesOptions}
            isLoading={props.isDocSeriesLoading}
            isRequired
            isNotClearable
            data-testid={suffixTestId('correctiveDocumentOutsideEu', props)}
          />
        </Grid>
      </VStack>
    </Card>
  );
}
